import { isSafariBrowser } from '@foundation-player/platform-check';
import { Environment, StreamType } from '@foundation-player/runtime';
import { ClientAndScope } from '@foundation-player/types';
import { BuilderConfig, PlayerType, UserConfig } from '../builder-config';
import { detectPlatform } from './detect-platform';
import { determineAdPlayerType } from './determine-ad-player-type';
import { determineStreamTypesByPreset } from './determine-stream-types-by-preset';
import { getRemoteConfig } from './get-remote-config';
import { mapToClientAndScope } from './map-to-client-and-scope';
import { transformPlayerConfig } from './player-config/transform-player-config';
import { transformPluginConfigs } from './plugin-configs/transform-plugin-configs';
import { selectPlayerTypeByStreamTypesAndPlatform } from './select-player-type-by-stream-types-and-platform';

export async function transformUserToBuilderConfig(userConfig: UserConfig): Promise<BuilderConfig> {
  const platform = userConfig.platform || detectPlatform();
  const environment = userConfig.environment || Environment.Production;

  let defaultStreamTypes: StreamType[] = [StreamType.DASH, StreamType.HLS];
  let clientAndScope: ClientAndScope | undefined;

  if ('client' in userConfig) {
    clientAndScope =
      'scope' in userConfig ? { client: userConfig.client, scope: userConfig.scope } : { client: userConfig.client };
  } else if ('preset' in userConfig) {
    clientAndScope = mapToClientAndScope(userConfig.preset, platform);
    defaultStreamTypes = determineStreamTypesByPreset(userConfig.preset);
  }

  let remoteConfig = userConfig.remoteConfig;

  if (!remoteConfig && clientAndScope) {
    remoteConfig = await getRemoteConfig(clientAndScope, environment);
  }

  const streamTypes = userConfig.streamTypes || defaultStreamTypes;
  let playerType =
    userConfig.playerType ||
    remoteConfig?.builderConfig?.playerType ||
    selectPlayerTypeByStreamTypesAndPlatform(streamTypes, platform);

  if (userConfig.useNativePlayerOnSafari && isSafariBrowser()) {
    // eslint-disable-next-line no-console
    console.warn(
      'Setting playerType to PlayerType.Native because useNativePlayerOnSafari is true and the browser is Safari',
    );
    playerType = PlayerType.Native;
  }

  const adPlayerType =
    userConfig.adPlayerType || remoteConfig?.builderConfig?.adPlayerType || determineAdPlayerType(playerType);

  const partialBuilderConfig: Omit<BuilderConfig, 'playerConfig' | 'pluginConfigs'> = {
    remoteConfig: remoteConfig || {},
    playerType,
    adPlayerType,
    bundleType: userConfig.bundleType,
    isLocal: userConfig.isLocal,
    cdn: userConfig.cdn,
  };

  const playerConfig = transformPlayerConfig(
    partialBuilderConfig,
    userConfig.playerConfig || {},
    clientAndScope,
    environment,
    platform,
  );
  const pluginConfigs = transformPluginConfigs(
    {
      ...partialBuilderConfig,
      playerConfig,
    },
    userConfig.pluginConfigs || {},
    clientAndScope,
    environment,
    platform,
  );

  return {
    ...partialBuilderConfig,
    playerConfig,
    pluginConfigs,
  };
}
